import type { CSSProperties } from 'react';
import type { z } from 'zod';
import type { TimesheetDay } from '@cloudsign/shared';
import { t } from '../../lib/i18n';
import { minutesToTime } from '../../components/phase2';

export type Segment = z.infer<typeof TimesheetDay>['segments'][number];

const DAY = 1440;
const STRIPES = 'repeating-linear-gradient(45deg, rgba(255,255,255,.7) 0 3px, transparent 3px 7px)';

type Kind = 'normal' | 'edited' | 'auto' | 'open';

/** Edited and automatic sign-outs are marked by pattern as well as colour, so colour is never the only cue. */
export function kindOf(s: Pick<Segment, 'edited' | 'autoSignedOut' | 'open'>): Kind {
  if (s.edited) return 'edited';
  if (s.autoSignedOut) return 'auto';
  if (s.open) return 'open';
  return 'normal';
}

const KIND_STYLE: Record<Kind, { className: string; style: CSSProperties }> = {
  normal: { className: 'bg-brand', style: {} },
  edited: { className: 'bg-amber-600', style: { backgroundImage: STRIPES } },
  auto: { className: 'bg-slate-600 border-r-4 border-slate-900', style: {} },
  open: { className: 'bg-brand', style: { backgroundImage: 'linear-gradient(90deg, transparent 55%, rgba(255,255,255,.65))' } },
};

/** A small coloured block used by the legend and the bar. */
export function Swatch({ kind }: { kind: Kind }) {
  const k = KIND_STYLE[kind];
  return <span aria-hidden="true" className={`inline-block h-3 w-6 rounded-sm ${k.className}`} style={k.style} />;
}

export function Legend() {
  const items: { kind: Kind; label: string }[] = [
    { kind: 'normal', label: t('rpt.ts.legend.normal') },
    { kind: 'edited', label: t('rpt.ts.legend.edited') },
    { kind: 'auto', label: t('rpt.ts.legend.auto') },
    { kind: 'open', label: t('rpt.ts.legend.open') },
  ];
  return (
    <ul className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-700" aria-label={t('rpt.ts.legend.title')}>
      {items.map((i) => (
        <li key={i.kind} className="inline-flex items-center gap-1.5">
          <Swatch kind={i.kind} />
          {i.label}
        </li>
      ))}
    </ul>
  );
}

/** Hour labels above the bars (every six hours). */
export function Axis() {
  return (
    <div aria-hidden="true" className="relative h-4 text-[11px] text-slate-600">
      {[0, 6, 12, 18, 24].map((h) => (
        <span key={h} className="absolute -translate-x-1/2 first:translate-x-0 last:-translate-x-full" style={{ left: `${(h / 24) * 100}%` }}>
          {minutesToTime(h * 60)}
        </span>
      ))}
    </div>
  );
}

function segmentLabel(s: Segment): string {
  const notes = [s.edited ? t('rpt.ts.edited') : '', s.autoSignedOut ? t('rpt.ts.auto') : '', s.open ? t('rpt.ts.open') : ''].filter(Boolean).join(', ');
  return t('rpt.ts.segment', { site: s.siteName, from: minutesToTime(s.startMinute), to: s.endMinute >= DAY ? '24:00' : minutesToTime(s.endMinute), notes }).trim();
}

/** One day as a 24-hour strip: each sign-in is a button laid along the hours (site time). */
export function DayBar({ segments, onPick }: { segments: Segment[]; onPick: (s: Segment) => void }) {
  return (
    <div className="relative h-7 overflow-hidden rounded-md bg-slate-100">
      {[25, 50, 75].map((p) => (
        <span key={p} aria-hidden="true" className="absolute inset-y-0 w-px bg-slate-300" style={{ left: `${p}%` }} />
      ))}
      {segments.map((s, i) => {
        const k = KIND_STYLE[kindOf(s)];
        const left = (Math.max(0, s.startMinute) / DAY) * 100;
        const width = Math.max(((Math.min(DAY, s.endMinute) - Math.max(0, s.startMinute)) / DAY) * 100, 0.8);
        const text = segmentLabel(s);
        return (
          <button
            key={`${s.visitId}-${i}`}
            type="button"
            title={text}
            aria-label={text}
            onClick={() => onPick(s)}
            className={`absolute inset-y-1 rounded-sm outline-offset-2 hover:brightness-110 focus-visible:outline-2 focus-visible:outline-slate-900 ${k.className}`}
            style={{ left: `${left}%`, width: `${width}%`, ...k.style }}
          />
        );
      })}
    </div>
  );
}
